import React from "react";
import { useViewContent } from "@/lib/pixel";
import { Wallet, GitBranch, DoorOpen } from "lucide-react";
import { PageHero, Reveal, Stagger, StaggerItem, Overline, SectionHeading, CTAButton } from "@/components/primitives";
import LeadDialog, { DOCS } from "@/components/forms/LeadDialog";
import { SponsorProposalBand } from "@/components/sections";
import { STALLS } from "@/data/site";
import { cn } from "@/lib/utils";

const WHY = [
  { icon: Wallet, t: "Buyers with budgets", d: "Pre-scheduled meetings with live projects." },
  { icon: GitBranch, t: "Project pipeline", d: "EPC, licensing, equipment and JV enquiries." },
  { icon: DoorOpen, t: "India market entry", d: "The cleanest single entry point for overseas firms." },
];

export default function Exhibit() {
  useViewContent("Exhibit Info");
  return (
    <>
      <PageHero
        overline="Exhibit"
        title="Get your technology in front of the people writing the cheques."
        subtitle="A one-day floor of project developers, producers, OMCs and investors with live projects."
      />

      <section className="bg-parchment py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <SectionHeading overline="Why exhibit" title="One day, the whole demand side." />
          <Stagger className="mt-14 grid gap-6 md:grid-cols-3">
            {WHY.map((w) => (
              <StaggerItem key={w.t}>
                <div className="flex h-full flex-col rounded-3xl border border-border bg-white p-8 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald2/10 text-emeraldDeep"><w.icon className="h-6 w-6" /></span>
                  <h3 className="mt-5 font-display text-xl font-bold text-navy">{w.t}</h3>
                  <p className="mt-2 text-navy/60">{w.d}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="border-y border-border bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <SectionHeading overline="Stall options" title="Pick your space." />
          <div className="mt-14 grid gap-6 lg:grid-cols-3">
            {STALLS.map((s, i) => (
              <Reveal key={s.name} delay={i * 0.08}>
                <div className={cn(
                  "relative flex h-full flex-col rounded-3xl border p-8 transition-all hover:-translate-y-1",
                  s.highlight ? "border-ethanolOrange bg-navy text-white shadow-2xl" : "border-border bg-parchment shadow-sm hover:shadow-xl"
                )}>
                  {s.highlight && <span className="absolute -top-3 left-8 rounded-full bg-ethanolOrange px-3 py-1 text-xs font-bold uppercase tracking-wide text-navy">Most visible</span>}
                  <h3 className={cn("font-display text-2xl font-bold", s.highlight ? "text-white" : "text-navy")}>{s.name}</h3>
                  <p className={cn("mt-3 flex-1", s.highlight ? "text-slate-300" : "text-navy/60")}>{s.desc}</p>
                  <p className={cn("mt-6 text-sm font-semibold", s.highlight ? "text-emerald-300" : "text-emeraldDeep")}>{s.meta}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10 flex flex-wrap gap-4">
            <LeadDialog
              formType="exhibit"
              title="Book an Exhibitor Stall"
              description="Tell us about your company and stall preference."
              ctaLabel="Submit enquiry"
              successMsg="Thanks — our expo team will be in touch with the floor plan."
              extraFields={[{ key: "stall", placeholder: "Stall preference (optional)" }]}
              trigger={<CTAButton data-testid="book-stall">Book an Exhibitor Stall</CTAButton>}
            />
            <LeadDialog
              formType="exhibit"
              title="Download the Sponsorship Proposal"
              description="Stall options, rates, tiers and deliverables — instant PDF download."
              ctaLabel="Get the proposal"
              successMsg="Sent. Your download is starting now."
              downloadUrl={DOCS.sponsorProposal.url}
              downloadName={DOCS.sponsorProposal.name}
              trigger={<CTAButton variant="outline" className="text-navy">Download the Proposal & Rates</CTAButton>}
            />
          </Reveal>
        </div>
      </section>

      <SponsorProposalBand title="Want more than a stall? Put your brand on the stage." />
    </>
  );
}
